import { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button, Logo, StripeBand } from '@/design/components';
import { formatPhone, normalizePhone } from '@/lib/phone';
import { requestOtp, verifyOtp } from './api';

const RESEND_SECONDS = 60;

export function OtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const phone = (location.state as { phone?: string } | null)?.phone ?? '';

  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const timer = window.setTimeout(() => setSecondsLeft((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);

  if (!phone) return <Navigate to="/login" replace />;

  const normalized = normalizePhone(phone);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    setIsVerifying(true);
    const result = await verifyOtp(phone, code.trim());
    setIsVerifying(false);

    if (result.ok) {
      navigate('/', { replace: true });
    } else {
      setError(result.message);
      setCode('');
      inputRef.current?.focus();
    }
  }

  async function onResend() {
    setError(null);
    const result = await requestOtp(phone);
    if (result.ok) {
      setNotice('שלחנו קוד חדש.');
      setSecondsLeft(RESEND_SECONDS);
    } else {
      setError(result.message);
    }
  }

  return (
    <div className="grid min-h-dvh grid-rows-[auto_1fr] bg-surface">
      <div className="bg-charcoal">
        <div className="flex h-32 items-center justify-center">
          <Logo on="charcoal" height={36} />
        </div>
        <StripeBand />
      </div>

      <main className="mx-auto grid w-full max-w-md content-start gap-6 p-4 pt-8">
        <div className="grid gap-2">
          <h1 className="m-0 font-display text-display-l font-semibold">הזן/י את הקוד</h1>
          <p className="m-0 text-body text-muted">
            שלחנו קוד ל{' '}
            <span dir="ltr" className="tabular">
              {normalized ? formatPhone(normalized) : phone}
            </span>
            .
          </p>
        </div>

        <form onSubmit={onSubmit} className="grid gap-4" noValidate>
          <div className="grid gap-1">
            <label htmlFor="code" className="text-label font-medium">
              קוד מה SMS
            </label>
            <input
              ref={inputRef}
              id="code"
              name="code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              dir="ltr"
              maxLength={8}
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
              aria-describedby={error ? 'code-error' : undefined}
              aria-invalid={error ? true : undefined}
              className="min-h-11 rounded-sm border border-muted bg-surface px-3 text-center font-display text-display-m tabular"
            />
            {error && (
              <p id="code-error" role="alert" className="m-0 text-body-sm text-bad">
                {error}
              </p>
            )}
            {notice && (
              <p role="status" className="m-0 text-body-sm text-good">
                {notice}
              </p>
            )}
          </div>

          <Button type="submit" variant="primary" block disabled={isVerifying || code.length < 4}>
            {isVerifying ? 'בודק' : 'כניסה'}
          </Button>
        </form>

        <div className="grid justify-items-start gap-2">
          <Button variant="ghost" onClick={onResend} disabled={secondsLeft > 0} className="px-0">
            {secondsLeft > 0 ? `שליחת קוד חדש בעוד ${secondsLeft} שניות` : 'שלח/י לי קוד חדש'}
          </Button>
          <Button variant="ghost" onClick={() => navigate('/login', { replace: true })} className="px-0">
            החלפת מספר טלפון
          </Button>
        </div>
      </main>
    </div>
  );
}
